import mixpanel from 'mixpanel-browser'
import type { UserService } from '@duck/api-definitions'
import { isClientDevEnv } from '@/client-env'
import type { NotebookAnalyticsEvent } from './notebookAnalytics'
import type { WorkspaceAnalyticsEvent } from './workspaceAnalytics'
import type { SqlAnalyticsEvent } from './sqlAnalytics'
import type { WorkspaceSettingsAnalyticsEvent } from './workspaceSettingsAnalytics'
import type { SidebarAnalyticsEvent } from './sidebarAnalytics'
import type { UserVoiceAnalyticsEvent } from './userVoiceAnalytics'
import type { FaqAnalyticsEvent } from './faqAnalytics'
import type { MarketingAnalyticsEvent } from './marketingAnalytics'
import type { AchievementAnalyticsEvent } from './achievementAnalytics'
import type { ChallengesAnalyticsEvent } from './challengesAnalytics'
import type { SupportedLanguage } from '@/modules/i18n/supportedLanguages'

export type ClientAnalyticsEvent =
  | 'loginCTA'
  | 'signupCTA'
  | 'language_changed'
  | 'workspace:save:failed'
  | 'workspace:telemetry:failed'
  | NotebookAnalyticsEvent
  | WorkspaceAnalyticsEvent
  | SqlAnalyticsEvent
  | FaqAnalyticsEvent
  | MarketingAnalyticsEvent
  | WorkspaceSettingsAnalyticsEvent
  | SidebarAnalyticsEvent
  | UserVoiceAnalyticsEvent
  | AchievementAnalyticsEvent
  | ChallengesAnalyticsEvent

export type ClientAnalyticsUserProperties = {
  Theme: 'light' | 'dark' | 'system'
  language: SupportedLanguage
}

const debugPrints = false && isClientDevEnv

// Accounts that must produce zero Mixpanel data (events, people properties,
// session recordings). Matched against user.email at identify time. Anonymous
// pre-auth activity is unavoidable for the first few events of a session
// because the email is not yet known.
const FULLY_EXCLUDED_EMAILS = new Set<string>([
  // Admins
  'ben@xp-lab.com',
  'talnmizrachi@gmail.com',
  'rubber.duck.analysis@gmail.com',
  'tal@xp-lab.com',
  'talnmizrachi+1@gmail.com',
  // Partners
  'ben+partner@xp-lab.com',
  'talnmizrachi+school@gmail.com',
  'tal+partner@xp-lab.com',
  // Users
  'tal+gscandidate@gmail.com',
  'tal+rounds@xp-lab.com',
  'talnmizrachi+ilcandidato@gmail.com',
  'talnmizrachi+irestore_candidate@gmail.com',
  'tal+candidate@xp-lab.com',
  'talnmizrachi+dataguild1@gmail.com',
  'ben.haran@gmail.com',
])

let initialized = false
let isFullyExcluded = false
let latestUserProperties: Partial<ClientAnalyticsUserProperties> = {}

export const ClientAnalytics = {
  get isInitialized() {
    return initialized
  },

  init() {
    // Skip Mixpanel on local previews of production builds (preview-prod-client:prerendered),
    // and anywhere a developer has explicitly opted out via localStorage.
    // /_mp/ is proxied by CloudFront in prod; serve-static has no proxy, so calls 404 and
    // session-replay enters a retry loop with malformed URLs that floods the terminal.
    if (typeof window !== 'undefined') {
      const { hostname } = window.location
      const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1'
      const explicitlyDisabled = window.localStorage.getItem('mp_disabled') === '1'
      if (isLocalhost || explicitlyDisabled) return
    }

    const token = import.meta.env.VITE_MIXPANEL_TOKEN

    if (!token && !isClientDevEnv) {
      throw new Error(
        'VITE_MIXPANEL_TOKEN is required for non-development builds'
      )
    }

    if (token && !isClientDevEnv) {
      mixpanel.init(token, {
        debug: false,
        track_pageview: false,
        persistence: 'localStorage',
        record_sessions_percent: 30,
        record_heatmap_data: true,
        record_mask_all_text: false,
        record_mask_text_selector: ['#billing-section'],
        // Mixpanel's default record_block_selector is 'img, video, audio', which
        // blocks images entirely. Drop 'img' so images are captured while video and
        // audio stay blocked; record_inline_images then base64-inlines the images so
        // they survive CORS on replay.
        record_block_selector: 'video, audio',
        record_inline_images: true,
        api_host: '/_mp',
        cdn: '/_mp',
      })
      initialized = true
    }
  },

  trackPageview() {
    if (!initialized || isFullyExcluded) return
    mixpanel.track_pageview({ page: window.location.pathname })
  },

  identify(user: UserService.User) {
    if (!initialized) return
    try {
      if (user.email && FULLY_EXCLUDED_EMAILS.has(user.email.toLowerCase())) {
        isFullyExcluded = true
        // Stop any sampled recording, drop the anonymous distinct_id so the
        // few pre-auth events stay unlinked from this user, and skip identify
        // entirely so no people profile is ever created.
        mixpanel.stop_session_recording()
        mixpanel.reset()
        return
      }
      mixpanel.identify(user.id)
      mixpanel.people.set({
        $email: user.email,
        $first_name: user.firstName,
        $last_name: user.lastName,
        role: user.role,
        source: user.source,
        organizationId: user.organizationId || 'no-org',
        ...latestUserProperties,
      })
      mixpanel.set_group('organizationId', user.organizationId || 'no-org')

      // Record all sessions for non-xp-lab users only (excludes @xp-lab.com and @test.xp-lab.com E2E users)
      if (!user.email?.endsWith('@xp-lab.com') && !user.email?.endsWith('@test.xp-lab.com')) {
        mixpanel.start_session_recording()
      }
    } catch {
      // Silently ignore analytics failures
    }
  },

  reset() {
    if (!initialized) return
    try {
      mixpanel.stop_session_recording()
      mixpanel.reset()
      latestUserProperties = {}
      // Clear the exclusion flag so the next user on this browser is tracked
      // normally — re-evaluated against the email list on their next identify.
      isFullyExcluded = false
    } catch {
      // Silently ignore analytics failures
    }
  },

  track(event: ClientAnalyticsEvent, properties?: Record<string, unknown>) {
    if (debugPrints) {
      console.log('📊', event, '📊', properties)
    }
    if (!initialized || isFullyExcluded) return
    try {
      mixpanel.track(event, properties)
    } catch {
      // Silently ignore analytics failures
    }
  },

  setUserProperties(properties: Partial<ClientAnalyticsUserProperties>) {
    if (debugPrints) {
      console.log('👤', properties)
    }
    latestUserProperties = { ...latestUserProperties, ...properties }
    if (!initialized || isFullyExcluded) return
    try {
      mixpanel.people.set(properties)
    } catch {
      // Silently ignore analytics failures
    }
  },
}
